/**
 * Staging Debug Tools
 * 
 * A debugging component to help diagnose staging deployment issues,
 * particularly for reprocessing workflow problems.
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Box,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Chip,
  Grid
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  BugReport as BugReportIcon,
  NetworkCheck as NetworkCheckIcon,
  Security as SecurityIcon,
  Api as ApiIcon
} from '@mui/icons-material';
import { auth } from '../../../../../firebase';
import { getApiUrl, debugApiConfig, getCurrentEnvironment, getApiBase } from '../../../../../utils/apiConfig';
import { PROCESSING_ENDPOINTS } from '../../constants/serviceEndpoints';

const StagingDebugTools = () => {
  const [results, setResults] = useState({});
  const [testSchoolId, setTestSchoolId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const getAuthHeaders = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    try {
      const token = await currentUser.getIdToken();
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  };

  const runEnvironmentCheck = async () => {
    setIsLoading(true);
    try {
      const envInfo = {
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        port: window.location.port,
        pathname: window.location.pathname,
        environment: getCurrentEnvironment(),
        apiBase: getApiBase(),
        processEnv: {
          REACT_APP_API_URL: process.env.REACT_APP_API_URL,
          REACT_APP_ENV: process.env.REACT_APP_ENV,
          NODE_ENV: process.env.NODE_ENV
        }
      };

      setResults(prev => ({ ...prev, environment: envInfo }));
    } catch (error) {
      setResults(prev => ({ ...prev, environment: { error: error.message } }));
    }
    setIsLoading(false);
  };

  const testAuthToken = async () => {
    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      const token = await user.getIdToken();
      const claims = await user.getIdTokenResult();
      
      const authInfo = {
        uid: user.uid,
        email: user.email,
        tokenLength: token.length,
        tokenPrefix: token.substring(0, 20) + '...',
        claims: {
          aud: claims.claims.aud,
          iss: claims.claims.iss,
          exp: new Date(claims.claims.exp * 1000).toISOString(),
          iat: new Date(claims.claims.iat * 1000).toISOString()
        },
        customClaims: Object.keys(claims.claims).filter(key => 
          !['aud', 'auth_time', 'exp', 'firebase', 'iat', 'iss', 'sub', 'user_id'].includes(key)
        )
      };

      setResults(prev => ({ ...prev, auth: authInfo }));
    } catch (error) {
      setResults(prev => ({ ...prev, auth: { error: error.message } }));
    }
    setIsLoading(false);
  };

  const testApiEndpoint = async (endpoint, method = 'GET', body = null) => {
    try {
      const authHeaders = await getAuthHeaders();
      const url = getApiUrl(endpoint);

      console.log(`Testing ${method} ${url}`);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        credentials: 'include',
        ...(body ? { body: JSON.stringify(body) } : {})
      });

      const contentType = response.headers.get('content-type');
      const responseHeaders = Object.fromEntries(response.headers.entries());

      let responseData;
      try {
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
        } else {
          const text = await response.text();
          responseData = {
            isHtml: text.includes('<!doctype') || text.includes('<html'),
            textPreview: text.substring(0, 200),
            fullText: text
          };
        }
      } catch (parseError) {
        responseData = { parseError: parseError.message };
      }

      return {
        url,
        status: response.status,
        statusText: response.statusText,
        contentType,
        headers: responseHeaders,
        data: responseData,
        ok: response.ok
      };
    } catch (error) {
      return {
        url: getApiUrl(endpoint),
        error: error.message,
        stack: error.stack
      };
    }
  };

  const testReprocessingEndpoint = async () => {
    setIsLoading(true);
    try {
      if (!testSchoolId.trim()) {
        throw new Error('Please enter a test school ID');
      }

      const testBody = {
        school_uuid: testSchoolId.trim(),
        services: ['esri']
      };

      const result = await testApiEndpoint(PROCESSING_ENDPOINTS.REPROCESSING, 'POST', testBody);
      setResults(prev => ({ ...prev, reprocessing: result }));
    } catch (error) {
      setResults(prev => ({ ...prev, reprocessing: { error: error.message } }));
    }
    setIsLoading(false);
  };

  const testHealthEndpoints = async () => {
    setIsLoading(true);
    try {
      const healthTests = await Promise.all([
        testApiEndpoint('/v2/cloud-run/health'),
        testApiEndpoint('/v2/system/health'),
        testApiEndpoint('/v2/system/version')
      ]);

      setResults(prev => ({ 
        ...prev, 
        health: {
          cloudRun: healthTests[0],
          system: healthTests[1],
          version: healthTests[2]
        }
      }));
    } catch (error) {
      setResults(prev => ({ ...prev, health: { error: error.message } }));
    }
    setIsLoading(false);
  };

  const runFullDiagnostic = async () => {
    setIsLoading(true);
    setResults({});
    
    try {
      debugApiConfig();
      await runEnvironmentCheck();
      await testAuthToken();
      await testHealthEndpoints();
    } catch (error) {
      console.error('Full diagnostic failed:', error);
    }
    
    setIsLoading(false);
  };

  const renderResult = (title, data) => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">{title}</Typography>
        {data?.error && <Chip label="Error" color="error" size="small" sx={{ ml: 2 }} />}
        {data?.ok === true && <Chip label="Success" color="success" size="small" sx={{ ml: 2 }} />}
        {data?.ok === false && <Chip label="Failed" color="error" size="small" sx={{ ml: 2 }} />}
      </AccordionSummary>
      <AccordionDetails>
        <Box component="pre" sx={{ 
          backgroundColor: 'grey.100', 
          p: 2, 
          borderRadius: 1, 
          overflow: 'auto',
          fontSize: '0.8rem',
          maxHeight: '300px'
        }}>
          {JSON.stringify(data, null, 2)}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Card>
      <CardHeader
        title="Staging Debug Tools"
        subheader="Diagnostic tools for troubleshooting staging deployment issues"
        avatar={<BugReportIcon />}
      />
      <CardContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          These tools help diagnose issues with API routing, authentication, and CORS in staging.
          Check the browser console for detailed logging output.
        </Alert>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              startIcon={<NetworkCheckIcon />}
              onClick={runEnvironmentCheck}
              disabled={isLoading}
              fullWidth
            >
              Environment Check
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              startIcon={<SecurityIcon />}
              onClick={testAuthToken}
              disabled={isLoading}
              fullWidth
            >
              Test Auth Token
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              startIcon={<ApiIcon />}
              onClick={testHealthEndpoints}
              disabled={isLoading}
              fullWidth
            >
              Test Health Endpoints
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              color="primary"
              onClick={runFullDiagnostic}
              disabled={isLoading}
              fullWidth
            >
              Run Full Diagnostic
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Test Reprocessing Endpoint</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Test School UUID"
                value={testSchoolId}
                onChange={(e) => setTestSchoolId(e.target.value)}
                placeholder="Enter a school UUID to test"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                onClick={testReprocessingEndpoint}
                disabled={isLoading || !testSchoolId.trim()}
                fullWidth
              >
                Test Reprocessing
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>Diagnostic Results</Typography>
          {Object.keys(results).length === 0 && (
            <Alert severity="info">No results yet. Run a diagnostic test to see results here.</Alert>
          )}
          {results.environment && renderResult('Environment Info', results.environment)}
          {results.auth && renderResult('Authentication', results.auth)}
          {results.health && renderResult('Health Endpoints', results.health)}
          {results.reprocessing && renderResult('Reprocessing Test', results.reprocessing)}
        </Box>
      </CardContent>
    </Card>
  );
};

export default StagingDebugTools;
